import React, { useState, useEffect } from "react";
import "./PlanSelectionModal.scss";
import { FaTimes, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import {
  getAllExistPlans,
  createPaymentOrder,
} from "../../services/api/SuperAdminApi";
import { getCompanyDataByID } from "../../services/api/CompanyApi";
import { useAuth } from "../../context/AuthContext";
import { formatText } from "../TruncateParties";

const PlanSelectionModal = ({ isOpen, companyId, onClose, onSelectPlan }) => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const { logout } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch plans
        const plansResponse = await getAllExistPlans("");
        const allPlans = plansResponse.data.result;

        // Filter plans by type and duration
        const filteredPlans = allPlans.filter((plan) =>
          ["ind", "std", "prm"].includes(plan.type)
        );

        setPlans(filteredPlans);

        // Fetch company data
        const companyResponse = await getCompanyDataByID(companyId);
        // Extract company object from response
        setCompanyData(companyResponse.data.company);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && companyId) {
      fetchData();
    }
  }, [isOpen, companyId]);

  // Auto-select current plan if it's premium
  useEffect(() => {
    if (companyData && plans.length > 0 && companyData.type === "prm") {
      const duration = billingPeriod === "yearly" ? 365 : 31;
      const premiumPlan = plans.find(
        (plan) => plan.type === "prm" && plan.duration === duration
      );
      if (premiumPlan) {
        setSelectedPlan(premiumPlan);
      }
    }
  }, [companyData, plans, billingPeriod]);

  const getFilteredPlans = () => {
    const duration = billingPeriod === "yearly" ? 365 : 31;
    return plans.filter((plan) => plan.duration === duration);
  };

  const getPlansByType = () => {
    const filteredPlans = getFilteredPlans();
    const planTypes = {
      ind: null,
      std: null,
      prm: null,
    };

    filteredPlans.forEach((plan) => {
      if (!planTypes[plan.type]) {
        planTypes[plan.type] = plan;
      }
    });

    return planTypes;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const shouldHighlight = (planType) => {
    if (!companyData?.type) return false;

    const planHierarchy = { ind: 1, std: 2, prm: 3 };
    const currentLevel = planHierarchy[companyData.type] || 0;
    const checkingLevel = planHierarchy[planType] || 0;

    return checkingLevel > currentLevel;
  };

  const shouldBlur = (planType) => {
    if (!companyData?.type) return false;

    const planHierarchy = { ind: 1, std: 2, prm: 3 };
    const currentLevel = planHierarchy[companyData.type] || 0;
    const checkingLevel = planHierarchy[planType] || 0;

    // Don't blur the current plan, only blur lower tier plans
    return checkingLevel < currentLevel;
  };

  const handleMakePayment = async () => {
    if (!selectedPlan || !termsAccepted || !companyId) {
      alert("Please select a plan and accept the terms and conditions.");
      return;
    }

    try {
      setLoading(true);

      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert(
          "Payment system failed to load. Please check your internet connection."
        );
        setLoading(false);
        return;
      }

      console.log("=== Starting Payment Process ===");
      console.log("Selected Plan:", selectedPlan);
      console.log("Company ID:", companyId);

      // Get Order Data
      const orderResponse = await createPaymentOrder(
        selectedPlan._id,
        companyId
      );
      console.log("=== Order Response ===");
      console.log(orderResponse.data);

      // Validate server response
      if (!orderResponse.data || typeof orderResponse.data !== "object") {
        throw new Error("Invalid response structure from payment API");
      }

      const { orderId, amount, currency, key } = orderResponse.data;

      // Validate required fields
      if (!orderId || !amount || !currency || !key) {
        alert("Payment system error. Please try again or contact support.");
        console.error("Missing required fields:", {
          orderId,
          amount,
          currency,
          key,
        });
        setLoading(false);
        return;
      }

      // Ensure amount is valid
      const validAmount = parseInt(amount);
      if (isNaN(validAmount) || validAmount <= 0) {
        alert("Invalid payment amount. Please try again.");
        console.error("Invalid amount:", amount);
        setLoading(false);
        return;
      }

      setLoading(false);

      // Razorpay options
      const options = {
        key: String(key).trim(),
        amount: parseInt(amount),
        currency: String(currency).trim().toUpperCase(),
        name: "Lawsuit Pro",
        description: "Plan Subscription Payment",
        order_id: String(orderId).trim(),
        handler: async function (response) {
          console.log("=== Payment Success ===");
          console.log("Razorpay Success Response:", response);

          // Check if razorpay_payment_id exists
          if (response.razorpay_payment_id) {
            // Payment successful
            setPaymentStatus("success");
            setStatusMessage(
              "Payment successful! Logging out and redirecting..."
            );
            setLoading(true);

            // Call the parent handler with success
            if (onSelectPlan) {
              onSelectPlan(selectedPlan, companyId, {
                ...orderResponse.data,
                razorpayResponse: response,
              });
            }

            // Logout and redirect after 2 seconds
            setTimeout(() => {
              logout();
              setTimeout(() => {
                window.location.href = "https://app.lawsuit-pro.com/";
              }, 500);
            }, 2000);
          } else {
            // Payment ID not found - something went wrong
            setPaymentStatus("error");
            setStatusMessage("Something went wrong. Please contact admin.");
            setLoading(false);
            console.error("Payment ID not found in response:", response);
          }
        },
        prefill: {
          name: companyData?.companyName || "Company User",
          email: companyData?.email || "",
          contact: companyData?.phno || "",
        },
        theme: {
          color: "#004A9A",
        },
        modal: {
          ondismiss: function () {
            console.log("Payment modal dismissed by user");
            setPaymentStatus("error");
            setStatusMessage("Payment cancelled.");
            setLoading(false);
          },
        },
      };

      console.log("=== Final Razorpay Options ===");
      console.log(options);

      // Initialize and open Razorpay
      try {
        const rzp = new window.Razorpay(options);

        rzp.on("payment.failed", function (response) {
          console.error("=== Payment Failed ===");
          console.error("Error Response:", response);
          setPaymentStatus("error");
          setStatusMessage(
            "Payment failed. Please try again or contact support."
          );
          setLoading(false);
        });

        console.log("Opening Razorpay checkout...");
        rzp.open();
      } catch (razorpayError) {
        console.error("=== Razorpay Initialization Error ===");
        console.error("Error:", razorpayError);
        setPaymentStatus("error");
        setStatusMessage(
          "Failed to initialize payment gateway. Please try again."
        );
        setLoading(false);
      }
    } catch (error) {
      console.error("=== Complete Error Details ===");
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);

      if (error.response) {
        console.error("Server Error Response:", error.response.data);
        console.error("Status Code:", error.response.status);
      }

      // Better error messages
      let errorMessage =
        "An error occurred during payment processing. Please try again.";

      if (error.message.includes("timeout")) {
        errorMessage = "Request timed out. Please try again in a few moments.";
      } else if (error.message.includes("Network Error")) {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.response && error.response.status >= 500) {
        errorMessage = "Server error occurred. Please try again later.";
      } else if (error.response && error.response.status === 404) {
        errorMessage = "Service not found. Please contact support.";
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      }

      setPaymentStatus("error");
      setStatusMessage(errorMessage);
      setLoading(false);
    }
  };

  const getCurrentPlanName = () => {
    if (!companyData || !companyData.planId) return "N/A";
    const currentPlan = plans.find((plan) => plan._id === companyData.planId);
    return currentPlan ? currentPlan.name : "N/A";
  };

  if (!isOpen) return null;

  const plansByType = getPlansByType();

  return (
    <div className="plan-selection-modal-overlay">
      <div className="plan-selection-modal">
        <div className="modal-header">
          <button
            className="close-button"
            onClick={onClose}
            disabled={paymentStatus === "success"}
          >
            &times;
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <p>
              {paymentStatus === "success"
                ? "Processing payment..."
                : "Loading plans..."}
            </p>
          </div>
        ) : (
          <>
            <div className="company-info">
              <h2>{formatText(companyData?.companyName) || "Company"}</h2>
              <div className="contact-info">
                <span>Email: {companyData?.email || "N/A"}</span>
                <span> | </span>
                <span>Phone: {companyData?.phno || "N/A"}</span>
              </div>
              <div className="current-plan">
                <span>
                  Current Plan: <strong>{getCurrentPlanName()}</strong>
                </span>
              </div>
            </div>

            <div className="plan-content">
              <h3 className="section-title">Select Plan *</h3>

              <div className="plans-grid">
                {plansByType.ind && (
                  <div
                    className={`plan-card ${
                      selectedPlan?._id === plansByType.ind._id
                        ? "selected"
                        : ""
                    } ${shouldBlur("ind") ? "blurred" : ""} ${
                      shouldHighlight("ind") ? "highlighted" : ""
                    }`}
                    onClick={() => setSelectedPlan(plansByType.ind)}
                  >
                    {shouldHighlight("ind") && (
                      <div className="upgrade-badge">Upgrade</div>
                    )}
                    <h4>Individual</h4>
                    <p className="price">
                      ₹{plansByType.ind.price.toLocaleString()}/
                      {billingPeriod === "yearly" ? "year" : "month"}
                    </p>
                  </div>
                )}

                {plansByType.std && (
                  <div
                    className={`plan-card ${
                      selectedPlan?._id === plansByType.std._id
                        ? "selected"
                        : ""
                    } ${shouldBlur("std") ? "blurred" : ""} ${
                      shouldHighlight("std") ? "highlighted" : ""
                    }`}
                    onClick={() => setSelectedPlan(plansByType.std)}
                  >
                    {shouldHighlight("std") && (
                      <div className="upgrade-badge">Upgrade</div>
                    )}
                    <h4>Standard</h4>
                    <p className="price">
                      ₹{plansByType.std.price.toLocaleString()}/
                      {billingPeriod === "yearly" ? "year" : "month"}
                    </p>
                  </div>
                )}

                {plansByType.prm && (
                  <div
                    className={`plan-card ${
                      selectedPlan?._id === plansByType.prm._id
                        ? "selected"
                        : ""
                    } ${shouldBlur("prm") ? "blurred" : ""} ${
                      shouldHighlight("prm") ? "highlighted" : ""
                    }`}
                    onClick={() => setSelectedPlan(plansByType.prm)}
                  >
                    {shouldHighlight("prm") && (
                      <div className="upgrade-badge">Upgrade</div>
                    )}
                    <h4>Premium</h4>
                    <p className="price">
                      ₹{plansByType.prm.price.toLocaleString()}/
                      {billingPeriod === "yearly" ? "year" : "month"}
                    </p>
                  </div>
                )}
              </div>

              <div className="billing-period-section">
                <label htmlFor="billing-period">Billing Period *</label>
                <select
                  id="billing-period"
                  value={billingPeriod}
                  onChange={(e) => {
                    setBillingPeriod(e.target.value);
                    setSelectedPlan(null); // Reset selection when changing period
                  }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="terms-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                  />
                  <span>
                    I have read and accept the{" "}
                    <a href="#terms" className="terms-link">
                      terms and conditions
                    </a>
                  </span>
                </label>
              </div>

              {/* Payment Status Message */}
              {paymentStatus && (
                <div className={`payment-status-message ${paymentStatus}`}>
                  <div className="status-icon">
                    {paymentStatus === "success" && <FaCheckCircle size={24} />}
                    {paymentStatus === "error" && (
                      <FaExclamationTriangle size={24} />
                    )}
                  </div>
                  <p>{statusMessage}</p>
                </div>
              )}

              <div className="modal-footer">
                <button
                  className="payment-button"
                  onClick={handleMakePayment}
                  disabled={
                    !selectedPlan ||
                    !termsAccepted ||
                    loading ||
                    paymentStatus === "success"
                  }
                >
                  {loading ? "Processing..." : "Make Payment"}
                </button>
                <p className="go-back">
                  Need to go back?{" "}
                  <button
                    onClick={onClose}
                    className="back-link"
                    disabled={paymentStatus === "success"}
                  >
                    Go Back
                  </button>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PlanSelectionModal;
